import React, { useState, useEffect, useMemo } from "react";
import VideoModal from "../components/VideoModal";
import "../styles/Pages.css";
import "../styles/HariParamrut.css";

const monthNameToNumber = {
  january: 1,
  february: 2,
  march: 3,
  april: 4,
  may: 5,
  june: 6,
  july: 7,
  august: 8,
  september: 9,
  october: 10,
  november: 11,
  december: 12,
};

const getMonthSortValue = (month) => {
  if (month === null || month === undefined) return 0;

  const monthText = String(month).trim();
  if (!monthText) return 0;

  const monthAsNumber = Number.parseInt(monthText, 10);
  if (!Number.isNaN(monthAsNumber)) return monthAsNumber;

  return monthNameToNumber[monthText.toLowerCase()] || 0;
};

const getYearSortValue = (year) => {
  const parsedYear = Number.parseInt(String(year), 10);
  return Number.isNaN(parsedYear) ? -Infinity : parsedYear;
};

const getDatePart = (dateValue, partIndex) => {
  if (!dateValue) return null;
  const parts = String(dateValue).split("-");
  if (parts.length !== 3) return null;
  return parts[partIndex] || null;
};

function PragatyaParv() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [selectedVideoTitle, setSelectedVideoTitle] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        //const response = await fetch("/dbihariprabodham.php?category=7");
        const response = await fetch(
          // "http://dbphp.prabodhswamiji.in/dbihariprabodham.php?category=7",
          "https://hariprabodham.org.in/api/?category=7",
        );

        if (!response.ok) {
          throw new Error("Failed to fetch videos");
        }
        const data = await response.json();

        // Transform the data to match our format
        const transformedVideos = Array.isArray(data)
          ? data
              .filter((video) => video?.VideoUrl && video?.Title)
              .map((video) => ({
                id: video.VideoUrl.replace(
                  "https://www.youtube.com/watch?v=",
                  "",
                ),
                title: video.Title,
                location: video.City || "",
                infoyear:
                  video.InfoYear ??
                  video.infoyear ??
                  getDatePart(video.InfoDate ?? video.infodate, 2) ??
                  "Unknown",
                infomonth:
                  video.InfoMonth ??
                  video.infomonth ??
                  getDatePart(video.InfoDate ?? video.infodate, 1) ??
                  null,
              }))
          : [];

        setVideos(transformedVideos);
        setError(null);
      } catch (err) {
        setError(err.message);
        setVideos([]);
        console.error("Error fetching videos:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  const openVideoModal = (videoId, title) => {
    setSelectedVideoId(videoId);
    setSelectedVideoTitle(title);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideoId(null);
    setSelectedVideoTitle(null);
  };

  const videosByYear = useMemo(
    () =>
      videos.reduce((groups, video) => {
        const year = video.infoyear || "Unknown";
        if (!groups[year]) groups[year] = [];
        groups[year].push(video);
        return groups;
      }, {}),
    [videos],
  );

  const sortedYears = useMemo(
    () =>
      Object.keys(videosByYear).sort(
        (a, b) => getYearSortValue(b) - getYearSortValue(a),
      ),
    [videosByYear],
  );

  return (
    <main className="page-content pravachan-page">
      <section className="page-header">
        <h1 className="section-title">
          Pragatya Parv
          {!loading && !error && (
            <span
              style={{ fontSize: "1rem", marginLeft: "12px", color: "#888" }}
            >
              ({videos.length})
            </span>
          )}
        </h1>
        <p className="header-subtitle">
          Pragat Guruhari Prabodhjivan Swamiji Pragatya Parv
        </p>
      </section>

      <section className="page-body">
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading videos...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>Error loading videos: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {sortedYears.map((year, index) => {
              const yearVideos = [...videosByYear[year]].sort(
                (a, b) =>
                  getMonthSortValue(b.infomonth) - getMonthSortValue(a.infomonth),
              );

              return (
                <section
                  key={year}
                  style={index === 0 ? undefined : { marginTop: "28px" }}
                >
                  <h2 style={{ marginBottom: "12px" }}>{year}</h2>
                  <div className="video-grid">
                    {yearVideos.map((video) => (
                      <div
                        key={`${video.id}-${video.title}`}
                        className="video-card"
                        onClick={() => openVideoModal(video.id, video.title)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => {
                          if (e.key === "Enter")
                            openVideoModal(video.id, video.title);
                        }}
                      >
                        <div className="video-thumbnail">
                          <img
                            src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                            alt={video.title}
                          />
                          <div className="play-overlay">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </div>
                        <div className="video-info">
                          <h3>{video.title}</h3>
                          {video.location && (
                            <p className="video-location">{video.location}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </section>

      {isModalOpen && (
        <VideoModal
          videoId={selectedVideoId}
          title={selectedVideoTitle}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </main>
  );
}

export default PragatyaParv;
